import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useDialog, useMobileMenu, usePlatformStore } from "@platform/store";
import { useHandleNavigationClick } from "@core/features/mobileMenu";
import {
  useFetchActions,
  getOrganizationsFromActions,
  getIncompleteActionCount,
  ACTION_TYPE_SLUG,
  type Action,
} from "@core/features/actions";
import { useUserMeetings } from "@platform/hooks/useUserMeetings";
import { useProgram } from "@platform/hooks/useProgram";
import { useIsEnrollmentClosed } from "@platform/hooks/useIsEnrollmentClosed";
import { PROGRAM_ROLE_SLUG } from "@core/features/program";
import { DashboardNavigationItems } from "./DashboardNavigationItems";

type InvitationFromAction = {
  id: number;
  shortcode: string;
  name?: string;
  max_uses?: number;
};

export function DashboardNavigationItemsContainer() {
  const { close: closeMobileMenu } = useMobileMenu();

  const { id, programId: programIdParam } = useParams<{ id: string; programId: string }>();
  const rawId = id || programIdParam;
  const programId = rawId ? Number(rawId) : undefined;

  const user = usePlatformStore((s) => s.user);
  const isChatDisabled = usePlatformStore((s) => s.featureFlags.isChatDisabled);
  const isSupportPageDisabled = usePlatformStore(
    (s) => s.featureFlags.isSupportPageDisabled
  );
  const isVideoDisabled = usePlatformStore(
    (s) => s.featureFlags.isVideoDisabled
  );
  // userCircles would come from user data or a separate fetch - currently not available in store
  const userCircles = undefined;

  const { openDialog } = useDialog();

  // Fetch user actions — scoped to current program when on a program route
  const { data: actionsData } = useFetchActions({
    programId,
    user,
    isEnabled: !!user,
  });

  // Find the send_invitation action and extract program ID
  const invitationProgramId = useMemo(() => {
    if (!actionsData?.actions) return null;

    const inviteFriendsAction = actionsData.actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.SEND_INVITATION
    );

    if (!inviteFriendsAction?.target_object_data) return null;

    const data = inviteFriendsAction.target_object_data as InvitationFromAction;
    if (!data.id || !data.shortcode) return null;

    return inviteFriendsAction.program;
  }, [actionsData?.actions]);

  // Find the schedule action and extract program ID
  const scheduleProgramId = useMemo(() => {
    if (!actionsData?.actions) return null;

    const scheduleAction = actionsData.actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.SCHEDULE
    );

    if (!scheduleAction) return null;

    return scheduleAction.program;
  }, [actionsData?.actions]);

  // Find the circle_view action and extract program ID
  const circleProgramId = useMemo(() => {
    if (!actionsData?.actions) return null;

    const circleAction = actionsData.actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.CIRCLE_VIEW
    );

    if (!circleAction) return null;

    return circleAction.program;
  }, [actionsData?.actions]);

  // Program nav shows if user has either Schedule or Circle action
  const rawProgramProgramId = scheduleProgramId || circleProgramId;

  // Hide Program/Profile/Resources links for users blocked by
  // EnrollmentClosedGate: withdrawn users, or users without the
  // onboarded_participant role while enrollment is closed.
  const { program } = useProgram();
  const isEnrollmentClosed = useIsEnrollmentClosed();
  const userRoles = program?.user_roles;
  const isEnrollmentClosedBlocked =
    userRoles?.includes(PROGRAM_ROLE_SLUG.WITHDRAWN_USER) ||
    (isEnrollmentClosed &&
      !userRoles?.includes(PROGRAM_ROLE_SLUG.ONBOARDED_PARTICIPANT));

  const programProgramId = isEnrollmentClosedBlocked
    ? null
    : rawProgramProgramId;

  // Get program ID for resources link (only shown if user has a resource action)
  const rawResourcesProgramId = useMemo(() => {
    if (!actionsData?.actions) return null;

    const resourceAction = actionsData.actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.WORKBOOK_DOWNLOAD ||
        action.action_type?.slug === ACTION_TYPE_SLUG.SOCIALMEDIAKIT_DOWNLOAD
    );

    return resourceAction?.program ?? null;
  }, [actionsData?.actions]);

  // Get program ID for profile link (always shown if user has any program)
  // Profile is always visible regardless of whether the profile action exists
  const rawProfileProgramId = useMemo(() => {
    if (!actionsData?.actions || actionsData.actions.length === 0) return null;
    // Use the program from the first action
    return actionsData.actions[0]?.program ?? null;
  }, [actionsData?.actions]);

  const resourcesProgramId = isEnrollmentClosedBlocked
    ? null
    : rawResourcesProgramId;
  const profileProgramId = isEnrollmentClosedBlocked
    ? null
    : rawProfileProgramId;

  // Find all org admin actions and extract organization IDs and names
  // Shows Organization Admin links for users with org admin related actions
  const organizations = useMemo(() => {
    if (!actionsData?.actions) return [];
    return getOrganizationsFromActions(actionsData.actions);
  }, [actionsData?.actions]);

  // Compute incomplete action count for Dashboard badge
  // Only counts visible actions (respects hide_from_dashboard display options)
  const incompleteActionCount = useMemo(
    () => getIncompleteActionCount(actionsData?.actions ?? []),
    [actionsData?.actions]
  );

  const handleSearchClick = () => {
    closeMobileMenu();

    openDialog("supportSearch");
  };

  const { meetings } = useUserMeetings();
  const hasVideoMeetings = !isVideoDisabled && meetings.length > 0;

  const { handleNavigationClick } = useHandleNavigationClick({
    closeMobileMenu,
  });

  return (
    <DashboardNavigationItems
      user={user}
      onOpenSearch={handleSearchClick}
      onNavigationClick={handleNavigationClick}
      isChatDisabled={isChatDisabled}
      isSupportPageDisabled={isSupportPageDisabled}
      userCircles={userCircles}
      invitationProgramId={invitationProgramId}
      programProgramId={programProgramId}
      resourcesProgramId={resourcesProgramId}
      profileProgramId={profileProgramId}
      supportProgramId={rawProfileProgramId}
      organizations={organizations}
      incompleteActionCount={incompleteActionCount}
      hasVideoMeetings={hasVideoMeetings}
    />
  );
}

export default DashboardNavigationItemsContainer;


